import { getSupplierUserSimpleList } from "@/apis/supplier";
import BaseProtable from "@/components/BaseProTable";
import { PrimaryButton } from "@/components/ui/buttons/PrimaryButton";
import CommonModal from "@/components/ui/CommonModal";
import {
  SUPPLIER_STAGE_OPTIONS,
  type SupplierStageCode,
  getSupplerStageText,
} from "@/constants";
import { useCompleteTable } from "@/hooks/baseProTable/useCompleteTable";
import type { SupplierUserSimpleListItem } from "@/types/apis";
import { notifyWarning } from "@/utils/notification";
import { TableProps } from "antd";
import { useEffect, useState } from "react";
import styles from "./index.module.less";

type Props = {
  open: boolean;
  onClose?: (pickedSupplier?: ListItem) => void;
  onOkMulti?: (pickedSuppliers?: ListItem[]) => void;
  fixedSupplierStage?: SupplierStageCode;
  multiple?: boolean;
};

type ListItem = SupplierUserSimpleListItem;

type SearchParams = {
  supplierName?: string;
  supplierUsName?: string;
  supplierCode?: string; // 供应商编码
  supplierStage?: SupplierStageCode;
};

const SelectSupplierModal: React.FC<Props> = ({
  open,
  onClose,
  onOkMulti,
  fixedSupplierStage,
  multiple,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [pickedSupplier, setPickedSupplier] = useState<ListItem | null>(null);
  const [pickedSuppliers, setPickedSuppliers] = useState<ListItem[]>([]);

  const onOk = () => {
    if (!pickedSupplier) {
      notifyWarning("请选择供应商");
      return;
    }
    setSelectedRowKeys([]);
    onClose?.(pickedSupplier);
  };

  const onMultiOk = () => {
    if (!pickedSuppliers.length) {
      notifyWarning("请选择供应商");
    }
    setSelectedRowKeys([]);
    onOkMulti?.(pickedSuppliers);
  };

  const [params, setParams] = useState<SearchParams>({});

  const onCancel = () => {
    const cancelAction = multiple ? onOkMulti : onClose;
    cancelAction?.();
    setParams({
      supplierStage: fixedSupplierStage,
    });
    setSelectedRowKeys([]);
    setPickedSupplier(null);
    setPickedSuppliers([]);
  };

  const { actionRef, fetchDatasource, columns, formRef } = useCompleteTable<
    ListItem,
    SearchParams
  >({
    fetchFn: getSupplierUserSimpleList,
    columns: [
      {
        title: "供应商阶段",
        dataIndex: "supplierStageName",
        key: "supplierStageName",
        width: 200,
        search: false,
        align: "center",
        render(_, record) {
          return <>{getSupplerStageText(record.supplierStage)}</>;
        },
      },
      {
        title: "供应商编号",
        dataIndex: "supplierCode",
        key: "supplierCode",
        width: 200,
        align: "center",
      },
      {
        title: "供应商名称",
        dataIndex: "supplierName",
        key: "supplierName",
        align: "center",
      },
      {
        title: "供应商阶段",
        dataIndex: "supplierStage",
        key: "supplierStage",
        hideInTable: true,
        valueType: "select",
        fieldProps: {
          options: SUPPLIER_STAGE_OPTIONS,
          disabled: fixedSupplierStage,
          defaultValue: fixedSupplierStage,
        },
      },
      {
        title: "供应商名称（英文）",
        dataIndex: "supplierUsName",
        key: "supplierUsName",
        align: "center",
        search: false,
      },
    ],
  });

  useEffect(() => {
    if (params.supplierStage) return;
    if (fixedSupplierStage) {
      setParams({
        supplierStage: fixedSupplierStage,
      });

      formRef.current?.setFieldsValue({
        supplierStage: fixedSupplierStage,
      });
    }
  }, [fixedSupplierStage, params, formRef]);

  const rowSelection: TableProps<ListItem>["rowSelection"] = {
    type: multiple ? "checkbox" : "radio",
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: ListItem[]) => {
      setSelectedRowKeys(selectedRowKeys);
      if (multiple) {
        setPickedSuppliers(selectedRows);
      } else {
        setPickedSupplier(selectedRows[0]);
      }
    },
  };

  const footer = () => (
    <div style={{ textAlign: "center" }}>
      <PrimaryButton onClick={multiple ? onMultiOk : onOk}>确定</PrimaryButton>
    </div>
  );

  return (
    <CommonModal
      title={"选择供应商"}
      open={open}
      onCancel={onCancel}
      width={1024}
      style={{ top: 20 }}
      footer={footer}
    >
      <BaseProtable<ListItem>
        headerTitle={null}
        actionRef={actionRef}
        formRef={formRef}
        columns={columns}
        className={styles.modalProTable}
        rowSelection={rowSelection}
        bordered
        pagination={false}
        request={fetchDatasource}
        params={params}
        onSubmit={(values: any) => {
          // 必须返回新对象引用
          setParams({ ...values });
        }}
      />
    </CommonModal>
  );
};

export default SelectSupplierModal;
